"use client";

import React, { useCallback, useState } from "react";
import { cn } from "utils";
import { OspToast } from "@ospweb/sdk-ui";
import { usePlaidConnectFlowMachine } from "./usePlaidConnectFlowMachine";
import type { PlaidConnectFlowOptions } from "./types";
import { useResponsiveChatOpen } from "@hooks/useResponsiveChatOpen";
import { useIsPc } from "@hooks/useIsPc";
import { buildPortfolioUploadHoldingsChatQuestion } from "@/src/components/PlaidImportModal/portfolioUploadHoldingsChatQuestion";
import { usePlaidLinkFlow } from "@/src/hooks/plaid/usePlaidLinkFlow";
import type { PlaidMemberAuthorizationSuccessOutcome } from "@/src/hooks/plaid/memberPlaidApi";
import { PlaidInstitutionRebindDialog } from "@/src/components/PlaidImportModal/PlaidInstitutionRebindDialog";

import { PlaidDenseWallStep } from "./steps/PlaidDenseWallStep";
import { PlaidCategorySelectStep } from "./steps/PlaidCategorySelectStep";
import { PlaidInvestListStep } from "./steps/PlaidInvestListStep";
import { PlaidBankListStep } from "./steps/PlaidBankListStep";
import { PlaidExitRetentionGeneralStep } from "./steps/PlaidExitRetentionGeneralStep";
import { PlaidExitRetentionPortfolioStep } from "./steps/PlaidExitRetentionPortfolioStep";
import { PlaidManualUploadStep } from "./steps/PlaidManualUploadStep";
import { Track } from "@/src/lib/tracking";
import log from "logbeacon";
import { getBridge } from "@/src/lib/bridge";
import { requestNativeSurface } from "@/src/features/nativeSurface/requestNativeSurface";
import { openAccountReadPendingModal } from "@components/AccountReadPending";

/** Figma `102803:64775` — broker 列表失败 toast；translate 待补 key */
const PLAID_CONNECT_FAILED_TOAST = "Account connected failed";

export interface PlaidConnectFlowModalProps {
  options: PlaidConnectFlowOptions;
  onCloseModal: () => void;
}

export const PlaidConnectFlowModal: React.FC<PlaidConnectFlowModalProps> = ({
  options,
  onCloseModal,
}) => {
  const [isPc] = useIsPc();
  const machine = usePlaidConnectFlowMachine(options);
  const {
    state,
    navigateTo,
    goBack,
    continueConnectFromRetention,
    triggerExitRetention,
    setAccountType,
    setSelectedInstitution,
  } = machine;

  const { openResponsiveChat } = useResponsiveChatOpen();

  const handleLinkSuccess = useCallback(
    async (outcome: PlaidMemberAuthorizationSuccessOutcome) => {
      try {
        await options.onSuccess?.({
          connection: outcome.connection,
          connectionId: outcome.connectionId || "",
          platform: "PLAID",
        });
      } catch (error) {
        Track.error(error, {
          tag: "PlaidConnectFlowModal",
          msg: "Plaid bind onSuccess callback failed",
        });
      }

      // 非 onboarding Plaid 连接成功（fe-spec §1.3 / §4.6 F9 / T8.3）：
      // 1. 若处于 onboarding：仅保留原 Account connected toast，不展示 pending 弹层（AC4）
      // 2. 若非 onboarding：
      //    - App 容器：向原生 SurfaceScheduler 注册 account_read_pending surface
      //    - Web 网页：打开 Web 端 AccountReadPendingModal 取代原 toast（AC1）
      // 3. confirm 链（requireAccountsConfirm，EDG-529 叠框修复）：bind 成功后链路
      //    会继续打开 My accounts 确认框——不在此处弹 pending 弹层，避免两层叠加。
      const fromOnboarding = Boolean(
        options.fromOnboarding || state.fromOnboarding,
      );
      const category = state.accountType ?? options.accountType;
      const side = category === "bank" ? "BANKING" : "INVEST";

      if (!fromOnboarding && !options.requireAccountsConfirm) {
        const bridge = getBridge();
        if (bridge.isInApp()) {
          requestNativeSurface({
            key: "account_read_pending",
            payload: {
              side,
              fromOnboarding: false,
            },
          });
        } else {
          setTimeout(() => {
            void openAccountReadPendingModal({ side });
          }, 300);
        }
      }

      onCloseModal();
    },
    [options, state.fromOnboarding, state.accountType, onCloseModal],
  );

  const handleLinkExit = useCallback(() => {
    // 挽留弹窗仅 onboarding 流程存在：非 onboarding 留在列表页（等价挽留「Continue connect」分支），
    // 用户可换机构重连，想退出点 X（X 不再弹挽留）。
    if (state.fromOnboarding) {
      triggerExitRetention();
    }
  }, [state.fromOnboarding, triggerExitRetention]);

  /** Failed ≠ 挽留：留在 broker 列表 + 错误 toast（Figma `102803:64775`） */
  const handleLinkError = useCallback((err: unknown) => {
    log.error(
      "[文案]Plaid link/bind failed:",
      err instanceof Error ? err.message : String(err),
    );
    OspToast.error({ content: PLAID_CONNECT_FAILED_TOAST });
  }, []);

  const [rebindUpdateLinkToken, setRebindUpdateLinkToken] = useState<
    string | null
  >(null);
  /** 点击机构名（或 "__others__"）：link_token + Plaid Link 打开空窗期该卡显示 spinner */
  const [linkingKey, setLinkingKey] = useState<string | null>(null);

  const {
    openLink,
    openLinkWithToken,
    loading: isPlaidLoading,
    binding: isPlaidBinding,
    webLinkHost,
  } = usePlaidLinkFlow({
    onBindSuccess: handleLinkSuccess,
    onBindError: handleLinkError,
    onLinkExit: handleLinkExit,
    onInstitutionAlreadyLinked: (updateLinkToken) => {
      setRebindUpdateLinkToken(updateLinkToken);
    },
    trackingContext: options.trackingContext,
  });

  const handleDirectLink = useCallback(
    async (brokerId?: number, linkingKey?: string) => {
      setLinkingKey(linkingKey ?? "__others__");
      try {
        await openLink({
          connectionId: options.connectionId,
          accountId: options.accountId,
          brokerId,
        });
      } catch (err) {
        handleLinkError(err);
      } finally {
        setLinkingKey(null);
      }
    },
    [openLink, options.connectionId, options.accountId, handleLinkError],
  );

  const handleAddManually = useCallback(() => {
    navigateTo("MANUAL_UPLOAD");
  }, [navigateTo]);

  /** 关闭只走 onCloseModal（即 openPlaidConnectFlowModal.finish），由 finish 统一调 options.onClose，避免双发。
   *  挽留弹窗仅 onboarding 流程存在：已在挽留页（二次退出）或非 onboarding → 直接关。 */
  const handleClose = useCallback(() => {
    if (
      state.currentStep.startsWith("EXIT_RETENTION") ||
      !state.fromOnboarding
    ) {
      onCloseModal();
    } else {
      triggerExitRetention();
    }
  }, [
    state.currentStep,
    state.fromOnboarding,
    triggerExitRetention,
    onCloseModal,
  ]);

  const renderStep = () => {
    switch (state.currentStep) {
      case "DENSE_WALL":
        return (
          <PlaidDenseWallStep
            onContinue={() => {
              if (options.accountType === "invest") {
                navigateTo("INVEST_LIST");
              } else if (options.accountType === "bank") {
                navigateTo("BANK_LIST");
              } else {
                navigateTo("CATEGORY_SELECT");
              }
            }}
            onClose={handleClose}
          />
        );
      case "CATEGORY_SELECT":
        return (
          <PlaidCategorySelectStep
            onSelectCategory={(cat) => {
              setAccountType(cat);
              if (cat === "invest") {
                navigateTo("INVEST_LIST");
              } else {
                navigateTo("BANK_LIST");
              }
            }}
            onBack={state.stepHistory.length > 1 ? goBack : undefined}
            onClose={handleClose}
          />
        );
      case "INVEST_LIST":
        return (
          <PlaidInvestListStep
            onSelectBroker={(broker) => {
              setSelectedInstitution(broker);
              handleDirectLink(broker.broker_id, broker.name ?? "");
            }}
            onConnectOthers={() => handleDirectLink()}
            onAddManually={handleAddManually}
            showAddManually={state.showAddManually}
            onBack={state.stepHistory.length > 1 ? goBack : undefined}
            onClose={handleClose}
            isLoading={isPlaidLoading || isPlaidBinding}
            linkingKey={linkingKey}
          />
        );
      case "BANK_LIST":
        return (
          <PlaidBankListStep
            onSelectBroker={(broker) => {
              setSelectedInstitution(broker);
              handleDirectLink(broker.broker_id, broker.name ?? "");
            }}
            onConnectOthers={() => handleDirectLink()}
            onAddManually={handleAddManually}
            showAddManually={state.showAddManually}
            onBack={state.stepHistory.length > 1 ? goBack : undefined}
            onClose={handleClose}
            isLoading={isPlaidLoading || isPlaidBinding}
            linkingKey={linkingKey}
          />
        );
      case "MANUAL_UPLOAD":
        return (
          <PlaidManualUploadStep
            onConfirm={(attachments) => {
              onCloseModal();
              if (options.onUploadSuccess) {
                void options.onUploadSuccess(attachments);
                return;
              }
              openResponsiveChat({
                question: buildPortfolioUploadHoldingsChatQuestion(),
                customParams: {
                  attachments,
                  source: "plaid_manual_upload",
                },
              });
            }}
            onBack={state.stepHistory.length > 1 ? goBack : undefined}
            onClose={handleClose}
          />
        );
      case "EXIT_RETENTION_GENERAL":
        return (
          <PlaidExitRetentionGeneralStep
            isMobile={!isPc}
            onContinueConnect={() => {
              continueConnectFromRetention();
            }}
            onAddManuallyInstead={handleAddManually}
            showAddManually={state.showAddManually}
            onConfirmExit={() => {
              options.onExit?.();
              onCloseModal();
            }}
            onClose={onCloseModal}
          />
        );
      case "EXIT_RETENTION_PORTFOLIO":
        return (
          <PlaidExitRetentionPortfolioStep
            onContinueConnect={() => {
              continueConnectFromRetention();
            }}
            onAddManuallyInstead={handleAddManually}
            showAddManually={state.showAddManually}
            onConfirmExit={() => {
              options.onExit?.();
              onCloseModal();
            }}
            onClose={onCloseModal}
          />
        );
      default:
        return null;
    }
  };

  const isGeneralExit = state.currentStep === "EXIT_RETENTION_GENERAL";

  return (
    <div
      className={cn(
        "relative flex flex-col overflow-hidden text-content_primary",
        isGeneralExit
          ? "h-[100dvh] w-screen bg-transparent"
          : isPc
            ? "h-[min(852px,90vh)] w-[539px] rounded-[24px] bg-background_solid_secondary shadow-2xl"
            : "h-[100dvh] w-screen bg-background_solid_secondary pt-safe",
      )}
    >
      {webLinkHost}
      {rebindUpdateLinkToken ? (
        <PlaidInstitutionRebindDialog
          onCancel={() => setRebindUpdateLinkToken(null)}
          onAdd={() => {
            const token = rebindUpdateLinkToken;
            setRebindUpdateLinkToken(null);
            if (token) {
              void openLinkWithToken(token);
            }
          }}
        />
      ) : null}
      {renderStep()}
    </div>
  );
};
